import React, { useState } from 'react';
import { Section } from './Section';
import { X, Check, ChevronDownIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
const comparisonData = [{
  sit: 'Alumno con contractura lumbar crónica',
  sin: 'Improvisás o evitás trabajar con él',
  con: 'Protocolo exacto: qué hacer, qué no hacer, qué decir'
}, {
  sit: 'Alumno con prótesis de cadera',
  sin: 'No sabés qué está contraindicado',
  con: 'Lista de restricciones y ejercicios adaptados por condición'
}, {
  sit: 'Alumna embarazada',
  sin: 'Miedo a lastimar o quedar mal',
  con: 'Guía por trimestre con todos los ejercicios adaptados'
}, {
  sit: 'Alumno con osteoporosis',
  sin: 'Evitás todo por las dudas',
  con: 'Protocolo claro de qué sí y qué no según el grado'
}, {
  sit: 'Situación de urgencia',
  sin: 'Pánico y sin saber qué hacer',
  con: 'Protocolo de emergencia paso a paso listo para usar'
}, {
  sit: 'Alumno nuevo con condición especial',
  sin: 'La ficha de anamnesis no te dice qué hacer',
  con: 'Tabla de equivalencias: si el PDF dice X, para esa condición hacé Y'
}];
const modules = [{
  icon: '🦴',
  title: 'Módulo 1 — Columna Vertebral',
  desc: 'Hernia de disco cervical, dorsal y lumbar. Escoliosis. Osteoporosis. Espondilolistesis. Post-cirugía de columna. Qué hacer, qué evitar y qué decir en cada caso.'
}, {
  icon: '🦵',
  title: 'Módulo 2 — Miembros Inferiores',
  desc: 'Artrosis de rodilla. Prótesis de cadera y rodilla. Lesión de LCA. Fascitis plantar. Adaptaciones ejercicio por ejercicio.'
}, {
  icon: '💪',
  title: 'Módulo 3 — Miembros Superiores',
  desc: 'Hombro congelado por fases. Síndrome del manguito rotador. Túnel carpiano. Artrosis de manos. Cómo modificar cada ejercicio sin romper la clase.'
}, {
  icon: '❤️',
  title: 'Módulo 4 — Condiciones Sistémicas',
  desc: 'Hipertensión arterial. Diabetes tipo 2. Fibromialgia. Lo que podés hacer, lo que no podés hacer y las señales de alerta que requieren parar.'
}, {
  icon: '🌸',
  title: 'Módulo 5 — Salud de la Mujer',
  desc: 'Embarazo trimestre por trimestre. Post-parto y diástasis abdominal. Incontinencia urinaria. Protocolo completo para cada etapa.'
}, {
  icon: '🚨',
  title: 'Módulo 6 — Herramientas de Seguridad',
  desc: 'Checklist de banderas rojas. Tabla maestra de equivalencias. Protocolo de emergencia listo para imprimir. Consentimiento informado editable para condiciones especiales.'
}];
export function CuerpoSeguroPro() {
  const [openModule, setOpenModule] = useState<number | null>(null);
  return <Section className="bg-brand-light !py-8 md:!py-12" data-id="element-75">
      <div className="text-center mb-6 md:mb-10" data-id="element-76">
        <h2 className="text-xl md:text-3xl font-display font-black text-white mb-2" data-id="element-77">
          ¿QUÉ AGREGA EL{' '}
          <span className="text-brand-accent" data-id="element-78">CUERPO SEGURO PRO™</span>?
        </h2>
        <p className="text-sm md:text-lg text-gray-300 max-w-3xl mx-auto" data-id="element-79">
          El sistema de adaptaciones que hace que ningún alumno sea "demasiado
          difícil"
        </p>
      </div>

      {/* Desktop table */}
      <div className="max-w-5xl mx-auto mb-8 md:mb-12 hidden md:block" data-id="element-80">
        <table className="w-full text-left border-collapse" data-id="element-81">
          <thead data-id="element-82">
            <tr data-id="element-83">
              <th className="p-2.5 border-b border-white/10 text-white font-display font-bold text-sm w-1/3" data-id="element-84">
                Situación
              </th>
              <th className="p-2.5 border-b border-white/10 text-red-400 font-display font-bold text-sm w-1/3 bg-red-500/5 rounded-tl-xl" data-id="element-85">
                Sin Cuerpo Seguro Pro
              </th>
              <th className="p-2.5 border-b border-white/10 text-brand-accent font-display font-bold text-sm w-1/3 bg-brand-accent/5 rounded-tr-xl" data-id="element-86">
                Con Cuerpo Seguro Pro
              </th>
            </tr>
          </thead>
          <tbody data-id="element-87">
            {comparisonData.map((row, i) => <tr key={i} className="border-b border-white/5" data-id="element-88">
                <td className="p-2.5 text-gray-300 font-medium text-xs" data-id="element-89">
                  {row.sit}
                </td>
                <td className="p-2.5 text-gray-400 bg-red-500/5 text-xs" data-id="element-90">
                  <div className="flex items-start" data-id="element-91">
                    <X className="w-3.5 h-3.5 text-red-400 mr-1.5 shrink-0 mt-0.5" data-id="element-92" />
                    {row.sin}
                  </div>
                </td>
                <td className="p-2.5 text-white bg-brand-accent/5 font-medium text-xs" data-id="element-93">
                  <div className="flex items-start" data-id="element-94">
                    <Check className="w-3.5 h-3.5 text-brand-accent mr-1.5 shrink-0 mt-0.5" data-id="element-95" />
                    {row.con}
                  </div>
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="max-w-lg mx-auto mb-8 md:hidden space-y-2" data-id="element-96">
        {comparisonData.map((row, i) => <div key={i} className="rounded-lg border border-white/5 overflow-hidden" data-id="element-97">
            <div className="px-3 py-1.5 bg-white/[0.03]" data-id="element-98">
              <p className="text-white text-xs font-medium" data-id="element-99">{row.sit}</p>
            </div>
            <div className="grid grid-cols-2 divide-x divide-white/5" data-id="element-100">
              <div className="px-2.5 py-2 bg-red-500/5" data-id="element-101">
                <div className="flex items-center gap-1 mb-0.5" data-id="element-102">
                  <X className="w-3 h-3 text-red-400" data-id="element-103" />
                  <span className="text-red-400/70 text-[9px] font-bold uppercase tracking-wider" data-id="element-104">
                    Sin
                  </span>
                </div>
                <p className="text-gray-400 text-[11px] leading-snug" data-id="element-105">
                  {row.sin}
                </p>
              </div>
              <div className="px-2.5 py-2 bg-brand-accent/5" data-id="element-106">
                <div className="flex items-center gap-1 mb-0.5" data-id="element-107">
                  <Check className="w-3 h-3 text-brand-accent" data-id="element-108" />
                  <span className="text-brand-accent/70 text-[9px] font-bold uppercase tracking-wider" data-id="element-109">
                    Con
                  </span>
                </div>
                <p className="text-white text-[11px] leading-snug font-medium" data-id="element-110">
                  {row.con}
                </p>
              </div>
            </div>
          </div>)}
      </div>

      {/* Modules as accordion */}
      <div className="max-w-3xl mx-auto" data-id="element-111">
        <h3 className="text-base md:text-xl font-display font-bold text-white mb-4 text-center flex items-center justify-center" data-id="element-112">
          <Check className="w-5 h-5 text-brand-accent mr-2 bg-brand-accent/20 p-0.5 rounded-full" data-id="element-113" />
          CUERPO SEGURO PRO™ INCLUYE:
        </h3>

        <div className="space-y-2" data-id="element-114">
          {modules.map((mod, i) => {
          const isOpen = openModule === i;
          return <div key={i} className="bg-brand-dark rounded-xl border overflow-hidden transition-all duration-200" style={{
            borderColor: isOpen ? 'rgba(45,212,191,0.3)' : 'rgba(255,255,255,0.05)'
          }} data-id="element-115">
                <button onClick={() => setOpenModule(isOpen ? null : i)} className="w-full flex items-center justify-between px-4 py-2.5 md:px-5 md:py-3 text-left focus:outline-none group" data-id="element-116">
                  <div className="flex items-center gap-2.5" data-id="element-117">
                    <span className="text-base md:text-lg" data-id="element-118">{mod.icon}</span>
                    <span className="text-white font-display font-bold text-xs md:text-sm" data-id="element-119">
                      {mod.title}
                    </span>
                  </div>
                  <motion.div animate={{
                rotate: isOpen ? 180 : 0
              }} transition={{
                duration: 0.2
              }} data-id="element-120">
                    <ChevronDownIcon className="w-4 h-4 text-gray-400 group-hover:text-brand-accent transition-colors" data-id="element-121" />
                  </motion.div>
                </button>
                <AnimatePresence initial={false} data-id="element-122">
                  {isOpen && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.25,
                ease: 'easeInOut'
              }} className="overflow-hidden" data-id="element-123">
                      <div className="px-4 pb-3 md:px-5 md:pb-4 pl-[2.75rem] md:pl-[3.25rem]" data-id="element-124">
                        <p className="text-gray-400 text-xs leading-relaxed" data-id="element-125">
                          {mod.desc}
                        </p>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>;
        })}
        </div>
      </div>
    </Section>;
}